
import React, { useState } from 'react';
import { Sparkles, Loader2, ArrowRightCircle, Target, Settings, Cpu, CheckCircle2, AlertTriangle, Waves, PlusCircle, Download, X, Copy, Check } from 'lucide-react';
import { generatePDiagram } from '../services/geminiService';
import { PDiagramData } from '../types';
import { useAppContext } from '../contexts/AppContext';
import { ModuleInfoModal } from './ModuleInfoModal';
import ActiveProjectBadge from './ActiveProjectBadge';

// --- Components ---

const ResultCard: React.FC<{
    title: string;
    icon: React.ReactNode;
    items: string[];
    className?: string;
    headerColor?: string;
    iconColor?: string;
}> = ({ title, icon, items, className = "", headerColor = "text-text-primary", iconColor = "text-primary" }) => (
    <div className={`bg-card rounded-lg shadow-sm border border-border flex flex-col h-full hover:shadow-md transition-shadow duration-300 ${className}`}>
        <div className="p-3 border-b border-border bg-background/50 rounded-t-lg">
            <h3 className={`font-bold text-base flex items-center gap-2 ${headerColor}`}>
                <span className={iconColor}>{icon}</span>
                {title}
            </h3>
        </div>
        <div className="p-3 flex-grow overflow-y-auto max-h-60">
            <ul className="space-y-1">
                {items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-text-secondary leading-relaxed">
                        <span className="block mt-1.5 w-1.5 h-1.5 min-w-[6px] rounded-full bg-border flex-shrink-0" />
                        <span>{item}</span>
                    </li>
                ))}
                {items.length === 0 && <li className="text-text-secondary italic text-sm">No items identified</li>}
            </ul>
        </div>
    </div>
);

const NoiseCategory: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
    <div className="bg-background p-3 rounded-md border border-border h-full">
        <h4 className="font-semibold text-primary text-xs mb-2 uppercase tracking-wide border-b border-border pb-1">{title}</h4>
        <ul className="space-y-1">
            {items.map((item, index) => (
                <li key={index} className="text-xs text-text-secondary leading-tight pl-2 border-l-2 border-primary/30">
                    {item}
                </li>
            ))}
            {items.length === 0 && <li className="text-text-secondary italic text-xs">None</li>}
        </ul>
    </div>
);

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: PDiagramData;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, data }) => {
    const [copied, setCopied] = useState(false);
    const jsonString = JSON.stringify(data, null, 2);

    const handleCopy = () => {
        navigator.clipboard.writeText(jsonString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-border flex justify-between items-center bg-background/50 rounded-t-lg">
                    <h3 className="font-bold text-lg text-text-primary flex items-center gap-2">
                        <Download size={20} className="text-primary" />
                        Export JSON
                    </h3>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-4 flex-grow overflow-hidden flex flex-col">
                    <p className="text-sm text-text-secondary mb-2">
                        Raw JSON data for this P-Diagram. You can use this for backups or external tools.
                    </p>
                    <div className="relative flex-grow">
                        <pre className="w-full h-full bg-background border border-border rounded-md p-4 text-xs font-mono overflow-auto text-text-primary">
                            {jsonString}
                        </pre>
                        <button
                            onClick={handleCopy}
                            className="absolute top-2 right-2 bg-primary text-white p-2 rounded hover:bg-primary/90 transition-colors shadow-sm"
                            title="Copy to Clipboard"
                        >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                    </div>
                </div>

                <div className="p-4 border-t border-border flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-background border border-border text-text-primary rounded hover:bg-background/80 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const ResultView: React.FC<{ data: PDiagramData; onSave: () => void; onExport: () => void }> = ({ data, onSave, onExport }) => {
    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 border-b border-border pb-4">
                <div className="text-center md:text-left">
                    <h2 className="text-2xl font-bold text-text-primary">{data.systemName}</h2>
                    <p className="text-text-secondary text-sm uppercase tracking-wider font-semibold">Generated P-Diagram</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onExport}
                        className="flex items-center gap-2 bg-background border border-border hover:bg-background/80 text-text-primary px-4 py-2 rounded-lg transition-colors shadow-sm font-medium"
                    >
                        <Download size={18} />
                        Export JSON
                    </button>
                    <button
                        onClick={onSave}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm font-medium"
                    >
                        <PlusCircle size={18} />
                        Save to Drafts
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-fr">
                <div className="md:col-start-1 md:row-start-1">
                    <ResultCard title="Input Signals" icon={<ArrowRightCircle size={18} />} items={data.inputSignals} className="border-t-4 border-t-indigo-500" />
                </div>
                <div className="md:col-start-2 md:row-start-1">
                    <ResultCard title="Goal" icon={<Target size={18} />} items={data.goal} className="border-t-4 border-t-emerald-500" />
                </div>
                <div className="md:col-start-3 md:row-start-1">
                    <ResultCard title="Control Factors" icon={<Settings size={18} />} items={data.controlFactors} className="border-t-4 border-t-blue-500" />
                </div>
                <div className="md:col-start-1 md:row-start-2">
                    <ResultCard title="Expected Output" icon={<CheckCircle2 size={18} />} items={data.expectedOutput} className="border-t-4 border-t-green-500" />
                </div>
                <div className="md:col-start-2 md:row-start-2">
                    <ResultCard title="System Function" icon={<Cpu size={18} />} items={data.systemFunction} className="border-t-4 border-t-violet-500" />
                </div>
                <div className="md:col-start-3 md:row-start-2">
                    <ResultCard title="Unexpected Output" icon={<AlertTriangle size={18} />} items={data.unexpectedOutput} className="border-t-4 border-t-rose-500" />
                </div>
            </div>

            <div className="bg-card rounded-xl border border-border p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-4 text-primary border-b border-border pb-2">
                    <Waves className="w-5 h-5" />
                    <h3 className="text-lg font-bold">Noise Factors</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <NoiseCategory title="Piece to Piece" items={data.noiseFactors.pieceToPiece} />
                    <NoiseCategory title="Change Over Time" items={data.noiseFactors.changeOverTime} />
                    <NoiseCategory title="Customer Usage" items={data.noiseFactors.customerUsage} />
                    <NoiseCategory title="External Environment" items={data.noiseFactors.externalEnvironment} />
                    <NoiseCategory title="Internal" items={data.noiseFactors.internal} />
                </div>
            </div>
        </div>
    );
};

// --- Main Page Component ---

const PDiagramGenerator: React.FC<{onGenerateDone: () => void}> = ({onGenerateDone}) => {
    const { addPDiagram, activeProject } = useAppContext();
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [generatedData, setGeneratedData] = useState<PDiagramData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    const handleGenerate = async () => {
        if (!description.trim()) return;

        setIsLoading(true);
        setError(null);
        setGeneratedData(null);

        try {
            const result = await generatePDiagram(description);
            setGeneratedData(result);
        } catch (err: any) {
            setError(err.message || "An unexpected error occurred. Please check your API Key configuration.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!generatedData) return;
        try {
            const dataToSave: PDiagramData = {
                ...generatedData,
                status: 'draft', // FORCE DRAFT STATUS
                projectName: activeProject !== 'all' ? activeProject : 'Default Project'
            };
            await addPDiagram(dataToSave);
            
            if(onGenerateDone) {
                onGenerateDone();
            }
            // We can't easily force the view mode to 'draft' from here via URL state without updating AppContext logic,
            // but the user will likely check drafts if they just saved a draft.
            // A toast or alert could help, but for now simple navigation is fine.
        } catch (e: any) {
            console.error(e);
            alert("Failed to save P-Diagram.");
        }
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-card p-6 rounded-lg border border-border shadow-sm gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
                        <Sparkles size={24} /> P-Diagram Generator
                    </h1>
                    <p className="text-text-secondary mt-1 text-sm">
                        Generate comprehensive Parameter Diagrams instantly from system descriptions using AI.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <ActiveProjectBadge />
                    <span className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full border border-primary/20 whitespace-nowrap">
                        Powered by Gemini
                    </span>
                </div>
                <ModuleInfoModal
                    title="AI P-Diagram Generator"
                    description="Instantly generate valid Parameter Diagrams from text descriptions using Gemini AI."
                    benefits={[
                        "Rapidly create baseline diagrams for new systems.",
                        "Identify potential noise factors you might have missed.",
                        "Save generated diagrams directly to your drafts."
                    ]}
                    usageInstructions={[
                        "Enter a system description in the text box.",
                        "Click 'Generate P-Diagram'.",
                        "Review the AI output and click 'Save to Drafts'."
                    ]}
                />
            </div>

            {/* Input Section */}
            {!generatedData && (
                <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-semibold flex items-center gap-2 text-text-primary mb-4">
                            <Sparkles size={18} className="text-primary" /> Generate New Diagram
                        </h2>

                        <div className="mb-6">
                            <label htmlFor="sys-desc" className="block text-sm font-medium text-text-secondary mb-2">
                                System Description & Context
                            </label>
                            <textarea
                                id="sys-desc"
                                className="w-full h-40 p-4 rounded-lg border border-border bg-background text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none transition-shadow text-sm"
                                placeholder="e.g., An electric power steering system for a passenger vehicle. It must provide variable assist based on vehicle speed, ensure fail-safe operation, and communicate with the vehicle's CAN bus."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="text-red-500 text-sm font-medium flex-1 mr-4">
                                {error}
                            </div>
                            <button
                                onClick={handleGenerate}
                                disabled={isLoading || !description.trim()}
                                className={`
                                flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition-all shadow-md
                                ${isLoading || !description.trim()
                                        ? 'bg-text-secondary/50 cursor-not-allowed'
                                        : 'bg-primary hover:bg-primary/90 hover:shadow-lg transform active:scale-95'
                                    }
                            `}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Analyzing System...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={18} />
                                        Generate P-Diagram
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    {isLoading && (
                        <div className="h-1 w-full bg-primary/20 overflow-hidden">
                            <div className="animate-progress w-full h-full bg-primary origin-left-right"></div>
                        </div>
                    )}
                </div>
            )}

            {/* Results Section */}
            {generatedData && (
                <>
                    <ResultView
                        data={generatedData}
                        onSave={handleSave}
                        onExport={() => setIsExportModalOpen(true)}
                    />
                    <ExportModal
                        isOpen={isExportModalOpen}
                        onClose={() => setIsExportModalOpen(false)}
                        data={generatedData}
                    />
                </>
            )}
        </div>
    );
};

export default PDiagramGenerator;
